/**
 * Feedback that has been sent, or is waiting to be: an outbox on this device.
 *
 * Pressing Send writes the feedback to localStorage FIRST and only then tries
 * the network, so a dropped connection, a server that is down, the hourly limit
 * or feedback being switched off never loses what somebody wrote. It is removed
 * when the server has taken it, and retried on a backoff otherwise - while the
 * app is open, when the browser says it is back online, when the app comes back
 * into view, and at the next launch.
 *
 * **A retry can never duplicate.** Every feedback carries a `clientId` made when
 * it was previewed, and the server keys the document on it: a send whose reply
 * was lost on the way back is taken again as the same feedback, not a second
 * one. That is also what makes two open tabs flushing the same outbox harmless.
 *
 * What is dropped instead of retried, and why:
 * - a refusal that would be the same every time (`PERMANENT`): the request
 *   itself is malformed, so waiting does not help;
 * - anything older than MAX_AGE_MS, and anything past MAX_QUEUED (oldest
 *   first): an outbox that grows without end on a device that is never online
 *   is a store of somebody's words nobody will read.
 *
 * The decisions are pure (`flushQueue` takes its storage, clock and sender) so
 * they can be tested; `startFeedbackOutbox` is the part wired to the browser.
 */
import type { FeedbackRequest } from "./feedback";

export const OUTBOX_KEY = "fiveout:feedbackOutbox";
export const MAX_QUEUED = 20;
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Waits before attempt n+1 (the last repeats): 1 min, 5 min, 15 min, 1 h, 6 h. */
export const BACKOFF_MS = [60_000, 5 * 60_000, 15 * 60_000, 60 * 60_000, 6 * 60 * 60_000] as const;

/** The server's refusals that no retry can change. Everything else is retried. */
const PERMANENT = new Set(["bad category", "feedback-too-long", "feedback-empty", "bad client id"]);

export interface QueuedFeedback {
  request: FeedbackRequest;
  queuedAtMs: number;
  attempts: number;
  /** Not before this. 0 for "now". */
  nextAttemptMs: number;
  /** The last refusal, for whoever reads the outbox while debugging. */
  lastError?: string;
}

/** The slice of `Storage` this needs, so tests can hand it a map. */
export interface OutboxStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isQueued(v: unknown): v is QueuedFeedback {
  const q = v as QueuedFeedback;
  return (
    !!q &&
    typeof q === "object" &&
    typeof q.queuedAtMs === "number" &&
    typeof q.attempts === "number" &&
    typeof q.nextAttemptMs === "number" &&
    !!q.request &&
    typeof q.request.clientId === "string" &&
    typeof q.request.text === "string"
  );
}

/** Never throws: storage that is missing, blocked or holding junk reads as empty. */
export function readQueue(storage: OutboxStorage | null): QueuedFeedback[] {
  try {
    const raw = storage?.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isQueued) : [];
  } catch {
    return [];
  }
}

/** False when the write did not happen (private mode, quota), which the dialog tells the player. */
export function writeQueue(storage: OutboxStorage | null, queue: QueuedFeedback[]): boolean {
  try {
    if (!storage) return false;
    if (queue.length === 0) storage.removeItem(OUTBOX_KEY);
    else storage.setItem(OUTBOX_KEY, JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

/** Adds one, replacing any entry with the same clientId, keeping the newest MAX_QUEUED. */
export function enqueue(storage: OutboxStorage | null, request: FeedbackRequest, nowMs: number): boolean {
  const queue = readQueue(storage).filter((q) => q.request.clientId !== request.clientId);
  queue.push({ request, queuedAtMs: nowMs, attempts: 0, nextAttemptMs: 0 });
  return writeQueue(storage, queue.slice(-MAX_QUEUED));
}

export function backoffAfter(attempts: number): number {
  return BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1]!;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function isPermanent(err: unknown): boolean {
  return PERMANENT.has(errorMessage(err));
}

/** What happened to each entry a flush looked at, by clientId. */
export type FlushResult = Map<string, { kind: "sent" } | { kind: "dropped"; error: string } | { kind: "kept"; error: string }>;

/**
 * Sends every entry that is due, oldest first, one at a time. Re-reads storage
 * around each send rather than holding one copy for the whole run: another tab
 * may be flushing too, and the player may queue more while a send is out.
 * `only` restricts it to one entry (the Send button's own attempt, which goes
 * now whatever its backoff says).
 */
export async function flushQueue(opts: {
  storage: OutboxStorage | null;
  now: () => number;
  send: (request: FeedbackRequest) => Promise<void>;
  only?: string;
}): Promise<FlushResult> {
  const { storage, now, send, only } = opts;
  const results: FlushResult = new Map();
  const fresh = (q: QueuedFeedback) => now() - q.queuedAtMs <= MAX_AGE_MS;
  // Expire before sending anything.
  const start = readQueue(storage);
  if (start.some((q) => !fresh(q))) writeQueue(storage, start.filter(fresh));

  const due = readQueue(storage).filter((q) => (only ? q.request.clientId === only : q.nextAttemptMs <= now()));
  for (const item of due) {
    const id = item.request.clientId;
    let outcome: { kind: "sent" } | { kind: "dropped"; error: string } | { kind: "kept"; error: string };
    try {
      await send(item.request);
      outcome = { kind: "sent" };
    } catch (err) {
      outcome = isPermanent(err) ? { kind: "dropped", error: errorMessage(err) } : { kind: "kept", error: errorMessage(err) };
    }
    results.set(id, outcome);
    const queue = readQueue(storage);
    if (outcome.kind === "kept") {
      writeQueue(
        storage,
        queue.map((q) => {
          if (q.request.clientId !== id) return q;
          const attempts = q.attempts + 1;
          return { ...q, attempts, nextAttemptMs: now() + backoffAfter(attempts), lastError: outcome.error };
        }),
      );
    } else {
      writeQueue(storage, queue.filter((q) => q.request.clientId !== id));
    }
  }
  return results;
}

/** When the next entry falls due, or null for an empty outbox. */
export function nextDueMs(storage: OutboxStorage | null): number | null {
  const queue = readQueue(storage);
  return queue.length ? Math.min(...queue.map((q) => q.nextAttemptMs)) : null;
}

/** A fresh feedback id. randomUUID needs a secure context, which a LAN dev URL is not. */
export function newClientId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  return [...b].map((x, i) => ([4, 6, 8, 10].includes(i) ? "-" : "") + x.toString(16).padStart(2, "0")).join("");
}

// ---- The browser side --------------------------------------------------------

function browserStorage(): OutboxStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

let chain: Promise<unknown> = Promise.resolve();
let timer: ReturnType<typeof setTimeout> | null = null;

async function sendOne(request: FeedbackRequest): Promise<void> {
  // Lazily: the network client builds Firebase at import, and launch should
  // not pay for that just to find an empty outbox.
  const { sendFeedback } = await import("../net/feedbackApi");
  await sendFeedback(request);
}

/** One flush at a time per tab; a call while one is running waits for it and then runs its own. */
function flush(only?: string): Promise<FlushResult> {
  const run = async () => {
    try {
      return await flushQueue({ storage: browserStorage(), now: Date.now, send: sendOne, only });
    } finally {
      schedule();
    }
  };
  const next = chain.then(run, run);
  chain = next.catch(() => undefined);
  return next;
}

function schedule() {
  if (timer) clearTimeout(timer);
  timer = null;
  const due = nextDueMs(browserStorage());
  if (due === null) return;
  // setTimeout's ceiling is ~24.8 days; the longest backoff is 6 h.
  timer = setTimeout(() => void flush(), Math.max(1_000, due - Date.now()));
}

/** Where a Send left the feedback. `queued` is safe on this device and will go later. */
export type SubmitResult =
  | { kind: "sent" }
  | { kind: "queued"; error: string }
  | { kind: "rejected"; error: string }
  | { kind: "unsaved"; error: string };

/**
 * The Send button: save, then try at once. A failure that is worth retrying
 * leaves it in the outbox and says so. If the save itself failed (storage
 * blocked) there is nothing to retry from, so the dialog keeps the words on
 * screen with Try again instead.
 */
export async function submitFeedback(request: FeedbackRequest): Promise<SubmitResult> {
  const saved = enqueue(browserStorage(), request, Date.now());
  if (!saved) {
    try {
      await sendOne(request);
      return { kind: "sent" };
    } catch (err) {
      return isPermanent(err) ? { kind: "rejected", error: errorMessage(err) } : { kind: "unsaved", error: errorMessage(err) };
    }
  }
  const result = (await flush(request.clientId)).get(request.clientId);
  if (!result || result.kind === "sent") return { kind: "sent" };
  return result.kind === "dropped" ? { kind: "rejected", error: result.error } : { kind: "queued", error: result.error };
}

/**
 * Retries the outbox for the life of the page: once shortly after launch, when
 * the browser reports the network is back, when the app comes back into view
 * (a home-screen app is resumed, not relaunched), and on the backoff timer.
 */
export function startFeedbackOutbox(): void {
  if (readQueue(browserStorage()).length) setTimeout(() => void flush(), 5_000);
  window.addEventListener("online", () => void flush());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && readQueue(browserStorage()).length) void flush();
  });
}
